/**
 * Shared Zod-free validators for signup/login/verify forms.
 *
 * Re-exported to both the backend (runtime validation) and the dashboard
 * (client-side pre-validation). The client uses these to render inline errors
 * before hitting the network; the backend uses its own zod schemas but follows
 * the same regex + length rules.
 *
 * @packageDocumentation
 */

/** RFC-compliant-ish email regex — "x@y.z" with no spaces. Finality is server-side. */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Max email length we accept; matches RFC 5321 practical limit. */
export const EMAIL_MAX_LENGTH = 254;

/** Minimum password length. Used in both UX hint and server validation. */
export const PASSWORD_MIN_LENGTH = 8;
/** Upper bound to avoid pathological argon2 runtimes on attacker-controlled input. */
export const PASSWORD_MAX_LENGTH = 200;

/** Minimum display name length. */
export const DISPLAY_NAME_MIN_LENGTH = 1;
/** Upper bound on display name to keep the dashboard/chat UIs readable. */
export const DISPLAY_NAME_MAX_LENGTH = 40;

/** Exactly 6 numeric digits. Matches the 6-digit OTC format. */
export const OTC_REGEX = /^\d{6}$/;

/** Result shape for the `validate*` helpers. `ok:true` means input passed. */
export interface FieldValidationResult {
  ok: boolean;
  /** Stable machine-readable code (e.g. `email_invalid`); present only on failure. */
  code?: string;
  /** Human-facing message suitable for inline rendering. */
  message?: string;
}

/**
 * Validate an email string client-side. Checks non-empty, length ceiling, and
 * the simple regex. The authoritative check is still server-side.
 */
export function validateEmail(email: string): FieldValidationResult {
  if (!email) return { ok: false, code: 'email_required', message: 'Email is required.' };
  if (email.length > EMAIL_MAX_LENGTH) {
    return { ok: false, code: 'email_too_long', message: 'Email is too long.' };
  }
  if (!EMAIL_REGEX.test(email)) {
    return { ok: false, code: 'email_invalid', message: 'Enter a valid email address.' };
  }
  return { ok: true };
}

/**
 * Validate a password's length only. Does not check strength — use
 * {@link passwordStrengthScore} for a visual meter.
 */
export function validatePassword(pw: string): FieldValidationResult {
  if (!pw) return { ok: false, code: 'password_required', message: 'Password is required.' };
  if (pw.length < PASSWORD_MIN_LENGTH) {
    return {
      ok: false,
      code: 'password_too_short',
      message: `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`,
    };
  }
  if (pw.length > PASSWORD_MAX_LENGTH) {
    return { ok: false, code: 'password_too_long', message: 'Password is too long.' };
  }
  return { ok: true };
}

/** Validate a display name's length. */
export function validateDisplayName(n: string): FieldValidationResult {
  if (!n || n.length < DISPLAY_NAME_MIN_LENGTH) {
    return { ok: false, code: 'display_name_required', message: 'Display name is required.' };
  }
  if (n.length > DISPLAY_NAME_MAX_LENGTH) {
    return {
      ok: false,
      code: 'display_name_too_long',
      message: `Display name must be ${DISPLAY_NAME_MAX_LENGTH} characters or fewer.`,
    };
  }
  return { ok: true };
}

/**
 * Heuristic password-strength score in the range `[0, 4]`.
 *
 * 0 = terrible, 4 = very strong. Used only for the UX meter; no server-side
 * enforcement depends on it. Intentionally lightweight (no zxcvbn dep) so the
 * dashboard bundle stays small.
 *
 * @param pw Candidate password.
 */
export function passwordStrengthScore(pw: string): number {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (pw.length >= 12) score += 1;
  if (pw.length >= 16) score += 1;
  const hasLower = /[a-z]/.test(pw);
  const hasUpper = /[A-Z]/.test(pw);
  const hasDigit = /\d/.test(pw);
  const hasSymbol = /[^\w\s]/.test(pw);
  const classes = [hasLower, hasUpper, hasDigit, hasSymbol].filter(Boolean).length;
  if (classes >= 3) score += 1;
  // Cap at 4
  return Math.min(score, 4);
}

/** Map a {@link passwordStrengthScore} value to a short label suitable for display. */
export function passwordStrengthLabel(score: number): string {
  return ['Very weak', 'Weak', 'Okay', 'Good', 'Strong'][score] ?? 'Weak';
}

/** Ordered pairs used to build {@link ERROR_CODE_MESSAGES}. */
const CODE_MSG_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['invalid_credentials', 'Invalid email or password.'],
  ['account_unverified', 'Please verify your email before logging in.'],
  ['account_disabled', 'This account has been disabled. Contact support.'],
  ['otc_invalid', 'That code is invalid or has expired.'],
  ['otc_wrong_purpose', 'This code was issued for a different purpose.'],
  ['rate_limited', 'Too many attempts. Try again in a moment.'],
  ['csrf_mismatch', 'Your session is stale. Refresh the page and try again.'],
  ['network_error', 'Network error. Check your connection and try again.'],
];

/** Frozen map of backend error code → user-facing message. */
export const ERROR_CODE_MESSAGES: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(CODE_MSG_PAIRS),
);

/**
 * Map a backend error code to a user-friendly message, falling back to the
 * supplied default if the code is unknown.
 */
export function friendlyError(code: string, fallback: string): string {
  return ERROR_CODE_MESSAGES[code] ?? fallback;
}
